import React from "react";
import { useTranslation } from "react-i18next";
import { fmtSigned } from "@/lib/journal";
import { Brain } from "@phosphor-icons/react";

const Block = ({ title, rows, testid, side }) => {
  const { t } = useTranslation();
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.realized_pnl)));
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">{title}</p>
      <div className="mt-4 space-y-4">
        {rows.length === 0 ? (
          <p className="font-mono text-[11px] text-zinc-600">{t("why.none")}</p>
        ) : null}
        {rows.map((r) => {
          const pos = r.realized_pnl >= 0;
          return (
            <div key={r.key} data-testid={`why-row-${testid}-${r.key}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-zinc-200">
                  {t(`why.${side}.${r.key}`, { defaultValue: r.label })}
                </span>
                <span className={`shrink-0 font-mono text-sm ${pos ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
                  {r.closed ? fmtSigned(r.realized_pnl) : "—"}
                </span>
              </div>
              <div className="mt-2 h-[3px] w-full bg-[#141414]">
                <div
                  className={`h-full transition-all duration-500 ${pos ? "bg-[#C58361]" : "bg-[#EF4444]"}`}
                  style={{ width: `${(Math.abs(r.realized_pnl) / max) * 100}%` }}
                />
              </div>
              <p className="mt-1.5 font-mono text-[10px] text-zinc-600">
                {t("why.rowStats", { count: r.trades, closed: r.closed, win: r.win_rate })}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const WhyAnalytics = ({ data }) => {
  const { t } = useTranslation();
  const buy = (data?.buy || []).filter((r) => r.trades > 0);
  const sell = (data?.sell || []).filter((r) => r.trades > 0);
  const empty = buy.length === 0 && sell.length === 0;

  return (
    <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="why-analytics-card">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("why.title")}</h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600">{t("why.subtitle")}</span>
      </div>

      {data?.insight_data?.length ? (
        <div className="mt-5 flex gap-3 border border-[#C58361]/30 bg-[#C58361]/[0.06] p-4" data-testid="why-key-insight">
          <Brain size={18} weight="duotone" className="mt-0.5 shrink-0 text-[#C58361]" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C58361]">{t("why.keyInsight")}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-zinc-200">
              {data.insight_data
                .map((i) =>
                  i.kind === "loss"
                    ? t("why.insightLoss", {
                        reason: t(`why.${i.side}.${i.key}`, { defaultValue: i.label }),
                        share: i.share,
                        pnl: fmtSigned(i.pnl),
                        count: i.closed,
                      })
                    : t("why.insightProfit", {
                        reason: t(`why.${i.side}.${i.key}`, { defaultValue: i.label }),
                        pnl: fmtSigned(i.pnl),
                        win: i.win_rate,
                      })
                )
                .join(" ")}
            </p>
          </div>
        </div>
      ) : null}

      {empty ? (
        <p className="mt-8 border border-dashed border-[#1F1F1F] px-4 py-10 text-center text-sm text-zinc-600">
          {t("why.empty")}
        </p>
      ) : (
        <div className="mt-6 grid gap-8 md:grid-cols-2">
          <Block title={t("why.whyBuy")} rows={buy} testid="buy" side="buy" />
          <Block title={t("why.whySell")} rows={sell} testid="sell" side="sell" />
        </div>
      )}

      {data?.unlabeled_buys > 0 && (
        <p className="mt-6 font-mono text-[10px] text-zinc-600" data-testid="why-unlabeled-hint">
          {t("why.unlabeled", { count: data.unlabeled_buys })}
        </p>
      )}
    </div>
  );
};
